import React, { useState, useEffect } from "react";
import { 
    Button, 
    Grid, 
    Row, 
    Col, 
    Glyphicon, 
    FormGroup,
    FormControl
} from "react-bootstrap";
import { View } from "react-native";
import {CopyToClipboard} from 'react-copy-to-clipboard';
import { getUsersAdminLookup } from "../utils/bedrockAPI";
import "./AllUsers.css";

export default function AllUsers({ history }) {
    const [allUsers, setAllUsers] = useState(null);
    const [usersLoading, setUsersLoading] = useState(false);
    const [userSearchTerm, setUserSearchTerm] = useState("");
    const [sortField, setSortField] = useState("firstName");
    const [sortDirection, setSortDirection] = useState("asc");
    const [hasLoaded, setHasLoaded] = useState(false);

    const loadAllUsers = async () => {
        if (hasLoaded || usersLoading) return;
        
        setUsersLoading(true);
        try {
            const users = await getUsersAdminLookup();
            // console.log(users);
            setAllUsers(users);
            setHasLoaded(true);
        } catch (error) {
            console.error('Error loading users:', error);
            setAllUsers([]);
            setHasLoaded(true);
        }
        setUsersLoading(false);
    };

    // Auto-load data when component mounts (when tab is clicked)
    useEffect(() => {
        loadAllUsers();
    }, []);

    function formatPhoneNumber(phone) {
        if (!phone) return '-';
        // Basic phone number formatting
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 10) {
            return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
        }
        return phone;
    }

    function getDisplayValue(value) {
        return value || '-';
    }

    function formatLastActionDate(lastActionDate) {
        if (!lastActionDate) return 'Never';
        const date = new Date(lastActionDate);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 1) return 'Today';
        if (diffDays === 2) return 'Yesterday';
        if (diffDays <= 7) return `${diffDays - 1} days ago`;
        if (diffDays <= 30) return `${Math.ceil((diffDays - 1) / 7)} weeks ago`;
        return date.toLocaleDateString();
    }

    function handleSort(field) {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    }

    function getSortIcon(field) {
        if (sortField !== field) return '';
        return sortDirection === 'asc' ? ' ▲' : ' ▼';
    }

    const filteredUsers = allUsers ? allUsers.filter(user => {
        if (!userSearchTerm) return true;
        
        const searchLower = userSearchTerm.toLowerCase();
        const firstName = user.firstName ? user.firstName.toLowerCase() : '';
        const lastName = user.lastName ? user.lastName.toLowerCase() : '';
        const email = user.email ? user.email.toLowerCase() : '';
        const phone = user.phone ? user.phone.toLowerCase() : '';
        const builderName = user.builderName ? user.builderName.toLowerCase() : '';
        
        return firstName.includes(searchLower) ||
               lastName.includes(searchLower) ||
               email.includes(searchLower) ||
               phone.includes(searchLower) ||
               builderName.includes(searchLower);
    }).sort((a, b) => {
        let aValue, bValue;
        let aIsNull = false, bIsNull = false;
        
        switch (sortField) {
            case 'firstName':
                aIsNull = !a.firstName;
                bIsNull = !b.firstName;
                aValue = aIsNull ? null : a.firstName.toLowerCase();
                bValue = bIsNull ? null : b.firstName.toLowerCase();
                break;
            case 'lastName':
                aIsNull = !a.lastName;
                bIsNull = !b.lastName;
                aValue = aIsNull ? null : a.lastName.toLowerCase();
                bValue = bIsNull ? null : b.lastName.toLowerCase();
                break;
            case 'lastActionDate':
                aIsNull = !a.lastActionDate;
                bIsNull = !b.lastActionDate;
                aValue = aIsNull ? null : new Date(a.lastActionDate);
                bValue = bIsNull ? null : new Date(b.lastActionDate);
                break;
            case 'builderName':
                aIsNull = !a.builderName;
                bIsNull = !b.builderName;
                aValue = aIsNull ? null : a.builderName.toLowerCase();
                bValue = bIsNull ? null : b.builderName.toLowerCase();
                break;
            default:
                return 0;
        }
        
        // Handle null values - put them last regardless of sort direction
        if (aIsNull && bIsNull) return 0;
        if (aIsNull) return 1;  // a is null, b is not - a goes last
        if (bIsNull) return -1; // b is null, a is not - b goes last
        
        // Both values are not null, proceed with normal sorting
        if (sortField === 'lastActionDate') {
            return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        } else {
            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        }
    }) : [];

    return (
        <View className="all-users-container">
            <div className="all-users-search">
                <FormGroup>
                    <div className="all-users-search-row">
                        <FormControl
                            type="text"
                            placeholder="Search by name, email, phone, or builder name..."
                            value={userSearchTerm}
                            onChange={(e) => setUserSearchTerm(e.target.value)}
                            className="all-users-search-input"
                            autoFocus
                        />
                        <Button
                            bsStyle="default"
                            onClick={() => setUserSearchTerm("")}
                            className="all-users-clear-button"
                        >
                            <Glyphicon glyph="remove" />
                        </Button>
                    </div>
                </FormGroup>
            </div>
            {hasLoaded && (
                <div className="all-users-count">
                    Users: {filteredUsers.length} of {allUsers ? allUsers.length : 0}
                </div>
            )}
            <Grid>
                <Row>
                    <Col md={10}>
                        {usersLoading ? (
                            <div className="all-users-loading">
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <div style={{ marginBottom: '20px' }}>
                                        <Glyphicon glyph="refresh" className="glyphicon-spin" style={{ fontSize: '24px', color: '#3498db' }} />
                                    </div>
                                    <div style={{ fontSize: '16px', color: '#666' }}>
                                        Loading users...
                                    </div>
                                </div>
                            </div>
                        ) : filteredUsers.length === 0 ? (
                            <div className="all-users-empty">
                                <div>No users found</div>
                            </div>
                        ) : (
                            <div className="all-users-table-container">
                                <Row className="all-users-header">
                                    <Col md={2}>
                                        <button 
                                            className="all-users-sort-button"
                                            onClick={() => handleSort('firstName')}
                                        >
                                            Name{getSortIcon('firstName')}
                                        </button>
                                    </Col>
                                    <Col md={4}>Email</Col>
                                    <Col md={2}>Phone</Col>
                                    <Col md={2}>
                                        <button 
                                            className="all-users-sort-button"
                                            onClick={() => handleSort('builderName')}
                                        >
                                            Builder{getSortIcon('builderName')}
                                        </button>
                                    </Col>
                                    <Col md={2}>
                                        <button 
                                            className="all-users-sort-button"
                                            onClick={() => handleSort('lastActionDate')}
                                        >
                                            Last Activity{getSortIcon('lastActionDate')}
                                        </button>
                                    </Col>
                                </Row>
                                {filteredUsers.map((user, i) => (
                                    <Row 
                                        key={user.userId || i} 
                                        className="all-users-row"
                                    >
                                        <Col md={2} className="all-users-name-cell">
                                            {user.builderId ? (
                                                <a 
                                                    href={`/accesscontrol/${user.builderId}/user/${user.userId}`}
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="all-users-name-button"
                                                    title={`View user: ${getDisplayValue(user.firstName)} ${getDisplayValue(user.lastName)}`}
                                                >
                                                    {getDisplayValue(user.firstName)} {getDisplayValue(user.lastName)}
                                                </a>
                                            ) : (
                                                <div className="all-users-name-text">
                                                    {getDisplayValue(user.firstName)} {getDisplayValue(user.lastName)}
                                                </div>
                                            )}
                                        </Col>
                                        <Col md={4}>
                                            <div className="all-users-email-container">
                                                <CopyToClipboard text={user.email}>
                                                    <Button 
                                                        bsSize="small"
                                                        bsStyle="default"
                                                    >
                                                        <Glyphicon glyph="duplicate" />
                                                    </Button>
                                                </CopyToClipboard>
                                                <span className="all-users-email-text">{user.email}</span>
                                            </div>
                                        </Col>
                                        <Col md={2}>
                                            {formatPhoneNumber(user.phone)}
                                        </Col>
                                        <Col md={2} className="all-users-builder-cell">
                                            {user.builderId ? (
                                                <a 
                                                    href={`/accesscontrol/${user.builderId}`}
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="all-users-builder-button"
                                                    title={`View builder: ${user.builderName}`}
                                                >
                                                    {user.builderName}
                                                </a>
                                            ) : (
                                                <div className="all-users-builder-text">
                                                    {getDisplayValue(user.builderName)}
                                                </div>
                                            )}
                                        </Col>
                                        <Col md={2}>
                                            {formatLastActionDate(user.lastActionDate)}
                                        </Col>
                                    </Row>
                                ))}
                            </div>
                        )}
                    </Col>
                </Row>
            </Grid>
        </View>
    );
}
